import { tracked } from "@supergrain/kernel/react"
import CardStackProvider from "react-app/components/planner/card-stacks/CardStackProvider"
import LessonLoading from "../components/planner/card-stacks/LessonLoading"
import UnifiedLessonEditable from "../components/planner/card-stacks/UnifiedLessonEditable"

interface Props {
  courseId: string
  planbookId: string
  rotationCalendarId: string
  dateString: string
  editCourseTitleAndOrder: (courseId: string) => void
  lessonsCollapsed: boolean
  perspective: "day" | "week" | "month"
  isFullScreen?: boolean
  /** Only show a lesson the day already has. */
  existingOnly?: boolean
  /** The caller's `{{else}}`: only full-screen, the detail pane and the routine wrote one. */
  showLoadingSkeleton?: boolean
  /**
   * The caller's `(not courseDate.hideFromCalendar)` gate: week, day, full-screen, the detail pane
   * and the routine wrote one; the sidebar, search and unit-comparison did not.
   */
  hideWhenHiddenFromCalendar?: boolean
  /** No drag pad, and no drag: a lesson shown out of its calendar has nowhere to move to. */
  dragDisabled?: boolean
}

/** The Ember `Lesson::Provider` + `LessonEditable` block, as a React island. */
export default tracked(function LessonIsland({
  courseId,
  planbookId,
  rotationCalendarId,
  dateString,
  editCourseTitleAndOrder,
  lessonsCollapsed,
  perspective,
  isFullScreen,
  existingOnly,
  showLoadingSkeleton,
  hideWhenHiddenFromCalendar,
  dragDisabled,
}: Props) {
  return (
    <CardStackProvider
      courseId={courseId}
      planbookId={planbookId}
      rotationCalendarId={rotationCalendarId}
      dateString={dateString}
    >
      {(courseDate, lesson, units, cardStackIsFulfilled, course, planbook, rotationCalendar) => {
        if (hideWhenHiddenFromCalendar && courseDate.hideFromCalendar) return null
        if (existingOnly && courseDate.cardStackId === null) return null
        if (!cardStackIsFulfilled || lesson === null) {
          return showLoadingSkeleton ? <LessonLoading /> : null
        }

        return (
          <UnifiedLessonEditable
            courseDate={courseDate}
            course={course}
            lesson={lesson}
            units={units}
            rotationCalendar={rotationCalendar}
            planbook={planbook}
            editCourseTitleAndOrder={editCourseTitleAndOrder}
            lessonsCollapsed={lessonsCollapsed}
            perspective={perspective}
            isFullScreen={isFullScreen}
            dragDisabled={dragDisabled}
          />
        )
      }}
    </CardStackProvider>
  )
})
